// src/layouts/components/NavCountBadge.tsx
/**
 * The count pill on a sidebar nav item.
 *
 * The **tone carries meaning**, so a glance at the sidebar says which numbers need attention rather than
 * just which ones are non-zero. Colours come from the theme's `semanticColors.states`, published as CSS
 * variables by `ThemeProvider`, so they track light and dark mode instead of being hardcoded hexes.
 *
 * Rendering nothing at 0 is deliberate. The hub distinguishes a key that is present and zero ("yours,
 * and there are none") from one that is absent ("not yours to see"), but a badge reading "0" is noise
 * either way, so both end up showing nothing. The present-versus-absent distinction still matters
 * upstream, in `MainSidebar`, which is what decides whether to mount this at all.
 *
 * Sized to hold **three digits** without crushing them, so counts up to 999 read cleanly and only
 * four-digit values fall back to "999+".
 */
import { Badge } from '@mantine/core';
import { badgeStyles } from '@/utils/tableStyles';

/** What the number means, not what colour it is. */
export type NavCountTone =
  /** Waiting on this person to act. Red. */
  | 'urgent'
  /** Work in flight and healthy. Green. */
  | 'active'
  /** Neutral totals and reference counts. Theme info colour. */
  | 'info'
  /** Needs attention soon, but nobody is blocked. Theme warning colour. */
  | 'warning';

const TONE_STYLE: Record<NavCountTone, { backgroundColor: string; color: string }> = {
  urgent:  { backgroundColor: 'var(--semantic-state-danger)',  color: '#ffffff' },
  active:  { backgroundColor: 'var(--semantic-state-success)', color: '#ffffff' },
  info:    { backgroundColor: 'var(--semantic-state-info)',    color: '#ffffff' },
  // Amber is too light for white text in either mode, so this one takes near-black.
  warning: { backgroundColor: 'var(--semantic-state-warning)', color: '#1a1a1a' },
};

export function NavCountBadge({
  count,
  tone = 'info',
}: {
  count?: number;
  tone?: NavCountTone;
}) {
  if (!count) return null;
  return (
    <Badge
      size="sm"
      radius="xl"
      variant="filled"
      // Not `circle`: that forces a fixed square, so anything past a single digit was crushed. A pill
      // with a minimum width keeps 1 and 2 digits looking round while letting 3 breathe.
      style={{ ...TONE_STYLE[tone], minWidth: 24, paddingInline: 7 }}
      styles={badgeStyles}
    >
      {count > 999 ? '999+' : count}
    </Badge>
  );
}
